import useSidebarState from '../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import LinkedInSetupModal from './LinkedIn/LinkedInSetupModal';
import { getLinkedInCompanyId } from '../utils/linkedInCompany';
import { hasHostedJobApplications } from '../utils/jobPostingApplication';
import { useAccessControl } from '../hooks/useAccessControl';
import { useAuth } from '../contexts/AuthContext';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import CalendarIntegration from './CalendarIntegration';
import { CALENDAR_ENABLED } from '../utils/featureFlags';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

const AppCell = ({ title, description, cta, disabled, onClick, logoSrc, logoAlt, logoStyle }) => (
  <div className="job-cell" style={{ height: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    {logoSrc === '/Google-Calendar-Logo-SVG_007.svg' && (
      <img src={logoSrc} alt={logoAlt} style={{ width: '70px', height: '40px', objectFit: 'fill', marginBottom: '10px', ...logoStyle }} />
    )}
    {logoSrc !== '/Google-Calendar-Logo-SVG_007.svg' && (
      <img src={logoSrc} alt={logoAlt} style={{ width: '40px', height: '40px', objectFit: 'contain', marginBottom: '10px', ...logoStyle }} />
    )}
    <div style={{ marginBottom: '20px' }}>
      <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '20px', color: 'var(--app-text, #FFFFFF)' }}>{title}</div>
      <p style={{ color: 'var(--app-text, #FFFFFF)', fontSize: '16px', marginTop: '20px', lineHeight: '24px' }}>{description}</p>
    </div>
    <button className={`post-job-cta${disabled ? '' : ''}`} onClick={onClick} disabled={disabled}>
      <i className={`fa-solid ${disabled ? 'fa-check' : 'fa-plug'}`}></i>
      <span>{cta}</span>
    </button>
  </div>
);

const ExternalApps = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation('externalApps');
  const { isRTL } = useLanguage();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [companyUID, setCompanyUID] = useState('');
  const [linkedInId, setLinkedInId] = useState('');
  const [showLinkedInSetup, setShowLinkedInSetup] = useState(false);
  const access = useAccessControl();
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [showCalendarIntegration, setShowCalendarIntegration] = useState('');

  useEffect(() => {
    const init = async () => {
      if (!currentUser) return;
      let uid = currentUser.uid;
      const ownerSnap = await get(ref(database, `companies/${uid}`));
      if (!ownerSnap.exists()) {
        const collabSnap = await get(ref(database, `collaborators/${uid}`));
        if (collabSnap.exists()) uid = collabSnap.val().companyId;
      }
      const company = uid === currentUser.uid ? ownerSnap : await get(ref(database, `companies/${uid}`));
      setLinkedInId(company.exists() ? getLinkedInCompanyId(company.val()) : '');
      setCompanyUID(uid);

      await loadConnections(uid);
    };
    init();
    // Do not auto-open the calendar modal on initial load
    if (window.location.hash === '#calendar') {
      setShowCalendarIntegration('');
      window.history.replaceState(null, '', '/external-apps');
    }
  }, [currentUser]);

  const loadConnections = async (uid) => {
    const connSnap = await get(ref(database, `companies/${uid}/calendarIntegrations`));
    if (connSnap.exists()) setCalendarConnections(Object.values(connSnap.val()));
    else setCalendarConnections([]);
  };

  const isConnected = (provider) => calendarConnections.some(c => (c.provider || '').toLowerCase().includes(provider));

  const handleConnect = (provider) => {
    setShowCalendarIntegration(provider || '');
  };

  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';

  return (
    <div style={{ height: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div className={`home-body ${isRTL ? 'rtl' : 'ltr'}`} style={{
        marginLeft: isRTL ? 'auto' : sidebarWidth,
        marginRight: isRTL ? sidebarWidth : 'auto',
        transition: 'all 250ms ease',
        width: `calc(100% - ${sidebarWidth})`,
        boxSizing: 'border-box',
        paddingTop: '100px',
        paddingInline: 'clamp(24px, 5vw, 80px)',
        overflowY: 'auto',
        height: '100vh',
        paddingBottom: '40px',
        display: 'block',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>

        <h2 className="manage-title">{t('title')}</h2>
        <div className="extensions-app-grid">
          <AppCell
            title={uiText("LinkedIn")}
            description={t('linkedin.description', 'Add your company page to set up LinkedIn job posting.')}
            cta={linkedInId ? t('linkedin.manage', 'Manage page') : t('linkedin.connect', 'Connect LinkedIn')}
            disabled={!companyUID || access.loading}
            onClick={() => setShowLinkedInSetup(true)}
            logoSrc={process.env.PUBLIC_URL + '/LI-Logo.png'}
            logoStyle={{ width: '120px', height: '40px' }}
            logoAlt="LinkedIn"
          />
          <AppCell
            title={t('google.title')}
            description={t('google.description')}
            cta={isConnected('google') ? t('cta.connected') : t('cta.connect')}
            disabled={isConnected('google')}
            onClick={() => handleConnect('google')}
            logoSrc={process.env.PUBLIC_URL + '/Google-Calendar-Logo-SVG_007.svg'}
            logoStyle={{ transform: 'scale(1.2)' }}
            logoAlt="Google Calendar"
          />
          <AppCell
            title={t('outlook.title')}
            description={t('outlook.description')}
            cta={isConnected('outlook') ? t('cta.connected') : t('cta.connect')}
            disabled={isConnected('outlook')}
            onClick={() => handleConnect('outlook')}
            logoSrc={process.env.PUBLIC_URL + '/outlook-cal.svg'}
            logoAlt="Outlook Calendar"
          />
          {['ChatGPT', 'Claude'].map(assistant => <AppCell
            key={assistant}
            title={assistant}
            description={t('talent.description', { assistant })}
            cta={t('cta.connect')}
            onClick={() => navigate(`/profile?tab=connections&assistant=${assistant.toLowerCase()}`)}
            logoSrc={`${process.env.PUBLIC_URL}/${assistant === 'ChatGPT' ? 'openai' : 'claude'}-logo.svg`}
            logoAlt={assistant}
            logoStyle={assistant === 'ChatGPT' ? { filter: 'var(--app-shadow-filter, invert(1))' } : undefined}
          />)}
        </div>
      </div>
      {showLinkedInSetup && (
        <LinkedInSetupModal
          companyUID={companyUID}
          companyId={linkedInId}
          hasAtsAccess={hasHostedJobApplications(access)}
          onClose={() => setShowLinkedInSetup(false)}
          onSaved={(id) => { setLinkedInId(id); setShowLinkedInSetup(false); }}
        />
      )}
      {CALENDAR_ENABLED && !!showCalendarIntegration && (
        <CalendarIntegration
          userUID={companyUID}
          onClose={() => setShowCalendarIntegration('')}
          onConnectionSuccess={async () => {
            setShowCalendarIntegration('');
            await loadConnections(companyUID);
          }}
          initialProvider={showCalendarIntegration || undefined}
        />
      )}
    </div>
  );
};

export default ExternalApps;
